import { useEffect } from 'react'
import { Routes, Route, useLocation, Outlet, Navigate } from 'react-router-dom'
import { Layout } from '@/components/Layout'
import { ClientShell } from '@/components/ClientShell/ClientShell'
import { AdminRoute } from '@/components/AdminRoute'
import { PlatformRoute } from '@/components/PlatformRoute'
import { RequireClientAuth } from '@/components/RequireClientAuth'
import { RequireProfissionalAuth } from '@/components/RequireProfissionalAuth'
import { StaffTarefasRoute } from '@/components/StaffTarefasRoute'
import { RequireTenantBound } from '@/components/RequireTenantBound'
import { ClientPublicRoot } from '@/components/ClientPublicRoot'
import { ClientFallbackRedirect } from '@/components/ClientFallbackRedirect'
import { TenantEntryPage } from '@/pages/TenantEntry'
import { AppHubPage } from '@/pages/AppHub/AppHubPage'
import { PrivacidadePage, TermosPage, EliminarContaPage } from '@/pages/Legal'
import { TENANT_ENTRY_SEGMENT } from '@/config/tenant'
import { CadastroPage } from '@/pages/CadastroPage'
import { LoginPage } from '@/pages/LoginPage'
import { RecuperarCodigoEntryPage, RecuperarCodigoPage } from '@/pages/RecuperarCodigoPage'
import { PerfilPage } from '@/pages/PerfilPage'
import { PacotesPage } from '@/pages/PacotesPage'
import { AgendamentoPage } from '@/pages/AgendamentoPage'
import { HistoricoPage } from '@/pages/HistoricoPage'
import { MenuInicialPage } from '@/pages/MenuInicialPage'
import { ServicosPage } from '@/pages/ServicosPage'
import { VIPPage } from '@/pages/VIPPage'
import { QuemSomosPage } from '@/pages/QuemSomosPage'
import { ContatosPage } from '@/pages/ContatosPage'
import { MapaPage } from '@/pages/MapaPage'
import { GaleriaPage } from '@/pages/GaleriaPage'
import { SocialPage } from '@/pages/SocialPage/SocialPage'
import { NotificacoesPage } from '@/pages/NotificacoesPage/NotificacoesPage'
import { ProfissionalDashboardPage } from '@/pages/Profissional/ProfissionalDashboardPage'
import { ProfissionalTarefasPage } from '@/pages/Profissional/ProfissionalTarefasPage'
import { ProfissionalLoginOrRedirect } from '@/pages/Profissional/ProfissionalLoginOrRedirect'
import { isPlatformPath } from '@/config/platform'
import {
  applyPlatformDocumentTheme,
  clearPlatformDocumentTheme,
  getPlatformTheme,
} from '@/utils/platformTheme'
import {
  applyMarketingLandingDocumentTheme,
  clearAppTheme,
  isMarketingLandingPath,
  loadAndApplyTheme,
} from '@/utils/theme'
import {
  AdminLoginOrRedirect,
  AdminLayout,
  AdminDashboardHome,
  AdminMenuPage,
  AdminIdentidadePage,
  AdminQrPage,
  AdminVisibilidadePage,
  AdminQuemSomosPage,
  AdminServicosPage,
  AdminCategoriasPage,
  AdminContatosPage,
  AdminMapaPage,
  AdminVipPage,
  AdminGaleriaPage,
  AdminUnidadesPage,
  AdminProfissionaisPage,
  AdminDisponibilidadePage,
  AdminAgendamentosPage,
  AdminPacotesPage,
  AdminTarefasPage,
  AdminTemaPage,
  AdminFeedbacksPage,
  AdminSocialPostsPage,
  AdminContaPage,
} from '@/pages/Admin'
import {
  PlatformEntry,
  PlatformLoginPage,
  PlatformLayout,
  PlatformOverview,
  PlatformTenantsPage,
  PlatformTenantDetailPage,
  PlatformNovaTenantPage,
} from '@/pages/Platform'
import { platformRouteSegment } from '@/config/platform'

function App() {
  const location = useLocation()
  const onPlatform = isPlatformPath(location.pathname)
  const onMarketingLanding = isMarketingLandingPath(location.pathname)

  useEffect(() => {
    if (onPlatform) {
      clearAppTheme()
      applyPlatformDocumentTheme(getPlatformTheme())
      return
    }
    clearPlatformDocumentTheme()
    if (onMarketingLanding) {
      applyMarketingLandingDocumentTheme()
      return
    }
    loadAndApplyTheme()
  }, [onPlatform, onMarketingLanding, location.pathname])

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<ClientPublicRoot />} />
        <Route path="/app" element={<AppHubPage />} />
        <Route path="/privacidade" element={<PrivacidadePage />} />
        <Route path="/termos" element={<TermosPage />} />
        <Route path="/eliminar-conta" element={<EliminarContaPage />} />
        <Route path={`/${TENANT_ENTRY_SEGMENT}/:tenantSlug`} element={<TenantEntryPage />} />
        <Route path="/admin">
          <Route index element={<AdminLoginOrRedirect />} />
          <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route path="dashboard" element={<AdminDashboardHome />} />
            <Route path="menu" element={<AdminMenuPage />} />
            <Route path="identidade" element={<AdminIdentidadePage />} />
            <Route path="qr" element={<AdminQrPage />} />
            <Route path="visibilidade" element={<AdminVisibilidadePage />} />
            <Route path="quem-somos" element={<AdminQuemSomosPage />} />
            <Route path="servicos" element={<AdminServicosPage />} />
            <Route path="categorias" element={<AdminCategoriasPage />} />
            <Route path="contatos" element={<AdminContatosPage />} />
            <Route path="mapa" element={<AdminMapaPage />} />
            <Route path="galeria" element={<AdminGaleriaPage />} />
            <Route path="unidades" element={<AdminUnidadesPage />} />
            <Route path="profissionais" element={<AdminProfissionaisPage />} />
            <Route path="disponibilidade" element={<AdminDisponibilidadePage />} />
            <Route path="agendamentos" element={<AdminAgendamentosPage />} />
            <Route path="pacotes" element={<AdminPacotesPage />} />
            <Route path="tarefas" element={<AdminTarefasPage />} />
            <Route path="tema" element={<AdminTemaPage />} />
            <Route path="feedbacks" element={<AdminFeedbacksPage />} />
            <Route path="social" element={<AdminSocialPostsPage />} />
            <Route path="conta" element={<AdminContaPage />} />
            <Route path="vip" element={<AdminVipPage />} />
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Route>
        </Route>
        <Route path={platformRouteSegment()}>
          <Route index element={<PlatformEntry />} />
          <Route path="login" element={<PlatformLoginPage />} />
          <Route element={<PlatformRoute><PlatformLayout /></PlatformRoute>}>
            <Route path="dashboard" element={<PlatformOverview />} />
            <Route path="tenants/novo" element={<PlatformNovaTenantPage />} />
            <Route path="tenants/:tenantId" element={<PlatformTenantDetailPage />} />
            <Route path="tenants" element={<PlatformTenantsPage />} />
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Route>
        </Route>
        <Route path="/profissional">
          <Route index element={<ProfissionalLoginOrRedirect />} />
          <Route
            path="painel"
            element={
              <RequireProfissionalAuth>
                <ProfissionalDashboardPage />
              </RequireProfissionalAuth>
            }
          />
          <Route
            path="tarefas"
            element={
              <RequireProfissionalAuth>
                <ProfissionalTarefasPage />
              </RequireProfissionalAuth>
            }
          />
          <Route path="*" element={<Navigate to="/profissional" replace />} />
        </Route>
        <Route path={`/${TENANT_ENTRY_SEGMENT}/:tenantSlug/recuperar-codigo`} element={<RecuperarCodigoEntryPage />} />
        <Route element={<RequireTenantBound><Outlet /></RequireTenantBound>}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/cadastro" element={<CadastroPage />} />
        <Route path="/recuperar-codigo" element={<RecuperarCodigoPage />} />
        <Route
          path="/menu"
          element={
            <RequireClientAuth>
              <ClientShell>
                <MenuInicialPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/perfil"
          element={
            <RequireClientAuth>
              <ClientShell>
                <PerfilPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/pacotes"
          element={
            <RequireClientAuth>
              <ClientShell>
                <PacotesPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/agendamento"
          element={
            <RequireClientAuth>
              <ClientShell>
                <AgendamentoPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/historico"
          element={
            <RequireClientAuth>
              <ClientShell>
                <HistoricoPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route path="/tarefas" element={<StaffTarefasRoute />} />
        <Route
          path="/servicos"
          element={
            <RequireClientAuth>
              <ClientShell>
                <ServicosPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/vip"
          element={
            <RequireClientAuth>
              <ClientShell>
                <VIPPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/quem-somos"
          element={
            <RequireClientAuth>
              <ClientShell>
                <QuemSomosPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/contatos"
          element={
            <RequireClientAuth>
              <ClientShell>
                <ContatosPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/mapa"
          element={
            <RequireClientAuth>
              <ClientShell>
                <MapaPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/galeria"
          element={
            <RequireClientAuth>
              <ClientShell>
                <GaleriaPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/social"
          element={
            <RequireClientAuth>
              <ClientShell>
                <SocialPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        <Route
          path="/notificacoes"
          element={
            <RequireClientAuth>
              <ClientShell>
                <NotificacoesPage />
              </ClientShell>
            </RequireClientAuth>
          }
        />
        </Route>
        <Route path="*" element={<ClientFallbackRedirect />} />
      </Routes>
    </Layout>
  )
}

export default App
